package hk.com.vr360_demo2.webapp.component.editor_rewrite.component

import emotion.react.css
import js.coroutines.awaitCancellation
import hk.com.vr360_demo2.webapp.Colors
import hk.com.vr360_demo2.webapp.component.common.attachHorizontalDragScroll
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaSceneConfig
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.useLayoutEffect
import react.useRef
import react.useState
import web.cssom.AlignItems
import web.cssom.Border
import web.cssom.Cursor
import web.cssom.Display
import web.cssom.FlexDirection
import web.cssom.JustifyContent
import web.cssom.LineStyle
import web.cssom.NamedColor
import web.cssom.ObjectFit
import web.cssom.Position
import web.cssom.ZIndex
import web.cssom.pct
import web.cssom.px
import web.html.HTMLDivElement
import web.html.HTMLElement
import kotlin.js.unsafeCast

private const val BAR_BOTTOM_OFFSET_PX = 32
private const val TOGGLE_GAP_ABOVE_BAR_PX = 12
// 10px padding + 64px thumb + 8px gap + 12px label + 10px padding
private const val BAR_CONTENT_HEIGHT_PX = 104
private val TOGGLE_BOTTOM_OFFSET_PX = BAR_BOTTOM_OFFSET_PX + BAR_CONTENT_HEIGHT_PX + TOGGLE_GAP_ABOVE_BAR_PX

external interface EditorBottomSceneSwitchBarProps : Props {
    var scenes: List<PanoramaSceneConfig>
    var currentSceneId: String?
    var onSwitchScene: (sceneId: String) -> Unit
}

val EditorBottomSceneSwitchBar = FC<EditorBottomSceneSwitchBarProps> { props ->
    val scrollRef = useRef<HTMLDivElement>(null)
    val dragDidMoveRef = useRef(false)
    var centered by useState(true)
    var contentVisible by useState(true)

    useLayoutEffect(dependencies = arrayOf(props.scenes, contentVisible)) {
        if (!contentVisible) return@useLayoutEffect
        val el = scrollRef.current
        if (el != null) {
            centered = el.scrollWidth <= el.clientWidth + 1
        }
    }

    useLayoutEffect(dependencies = arrayOf(props.scenes, contentVisible)) {
        if (!contentVisible) return@useLayoutEffect
        val el = scrollRef.current ?: return@useLayoutEffect
        val detach = el.attachHorizontalDragScroll(dragDidMoveRef)
        awaitCancellation(detach)
    }

    button {
        title = "選擇場景"
        onClick = { contentVisible = !contentVisible }
        css {
            position = Position.fixed
            left = 20.px
            bottom = (TOGGLE_BOTTOM_OFFSET_PX+5).px
            zIndex = 1001.asDynamic().unsafeCast<ZIndex>()
            asDynamic().pointerEvents = "auto"
            display = Display.flex
            alignItems = AlignItems.center
            justifyContent = JustifyContent.center
            width = 40.px
            height = 40.px
            borderRadius = 50.pct
            backgroundColor = Colors.Background.Semitransparent
            border = 0.px
            cursor = Cursor.pointer
            padding = 0.px
            asDynamic().outline = "none"
        }
        img {
            src = "/assets/images/switch.svg"
            alt = "選擇場景"
            css {
                width = 24.px
                height = 24.px
                display = Display.block
            }
        }
    }

    if (contentVisible) {
        div {
            css {
                position = Position.fixed
                left = 0.px
                right = 0.px
                bottom = BAR_BOTTOM_OFFSET_PX.px
                zIndex = 1000.asDynamic().unsafeCast<ZIndex>()
                backgroundColor = Colors.Background.Semitransparent
                asDynamic().boxShadow = "0 0 16px rgba(0,0,0,0.15)"
                padding = 10.px
                asDynamic().pointerEvents = "none"
            }

            div {
                ref = scrollRef
                css {
                    asDynamic().pointerEvents = "auto"
                    display = Display.flex
                    flexDirection = FlexDirection.row
                    asDynamic().overflowX = "auto"
                    asDynamic().overflowY = "hidden"
                    gap = 24.px
                    justifyContent = if (centered) JustifyContent.center else JustifyContent.flexStart
                    asDynamic().transition = "justify-content 0.2s"
                    asDynamic().scrollbarWidth = "none"
                    asDynamic().msOverflowStyle = "none"
                    asDynamic()["&::-webkit-scrollbar"] = js("{}").unsafeCast<react.CSSProperties>().apply {
                        asDynamic().display = "none"
                    }
                    cursor = Cursor.grab
                    asDynamic().userSelect = "none"
                    asDynamic()["touchAction"] = "pan-x"
                    asDynamic()["@media (max-width: 600px)"] = js("{}").unsafeCast<react.CSSProperties>().apply {
                        justifyContent = JustifyContent.flexStart
                    }
                }

                props.scenes.forEach { scene ->
                    val isActive = scene.id == props.currentSceneId
                    button {
                        key = scene.id.unsafeCast<Key>()
                        onClick = {
                            if (dragDidMoveRef.current == true) {
                                dragDidMoveRef.current = false
                            } else {
                                props.onSwitchScene(scene.id)
                            }
                        }
                        css {
                            display = Display.flex
                            flexDirection = FlexDirection.column
                            alignItems = AlignItems.center
                            asDynamic().border = "0"
                            asDynamic().background = "transparent"
                            cursor = Cursor.pointer
                            asDynamic().outline = "none"
                            padding = 0.px
                        }

                        img {
                            src = scene.cover ?: "/assets/images/default-avatar.svg"
                            alt = scene.name ?: scene.id
                            asDynamic().draggable = false
                            css {
                                width = 64.px
                                height = 64.px
                                objectFit = ObjectFit.cover
                                border = Border(
                                    2.px,
                                    LineStyle.solid,
                                    if (isActive) Colors.Stroke.Primary else Colors.Stroke.White
                                )
                            }
                            onError = {
                                val target = it.target as? HTMLElement
                                target?.setAttribute("src", "/assets/images/default-avatar.svg")
                            }
                        }

                        div {
                            css {
                                marginTop = 8.px
                                fontSize = 12.px
                                color = NamedColor.white
                                asDynamic().maxWidth = "72px"
                                asDynamic().whiteSpace = "nowrap"
                                asDynamic().overflow = "hidden"
                                asDynamic().textOverflow = "ellipsis"
                                asDynamic().textAlign = "center"
                            }
                            +(scene.name ?: scene.id)
                        }
                    }
                }
            }
        }
    }
}
